import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  LineChart, Line, AreaChart, Area
} from 'recharts';

// =============================================================================
// DESIGN TOKENS - Same as OtelMetricsPage
// =============================================================================

const theme = {
  colors: {
    bg: '#0d0814',
    card: '#1a1225',
    cardHover: '#251a35',
    border: '#3d2a54',
    borderLight: '#5a4a6e',
    primary: '#64ffda',      // OTEL / Current
    primaryDim: 'rgba(100, 255, 218, 0.2)',
    secondary: '#ff9f43',    // GitHub / Historical
    secondaryDim: 'rgba(255, 159, 67, 0.2)',
    accent: '#a855f7',
    success: '#00d9a4',
    warning: '#ffc107',
    danger: '#ff6b6b',
    text: '#f5f0e1',
    textMuted: '#b8a8c8',
    textDim: '#7a6a8a',
  },
  fonts: {
    header: "'Press Start 2P', cursive",
    body: "'VT323', monospace",
  },
};

// =============================================================================
// PIXEL CARD COMPONENT (reused)
// =============================================================================

function PixelCard({ children, className = '', hover = true, glow = false, style }) {
  return (
    <div
      className={`pixel-card ${className}`}
      style={{
        backgroundColor: theme.colors.card,
        border: '2px solid',
        borderColor: theme.colors.border,
        position: 'relative',
        transition: 'all 0.15s steps(2)',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        ...(glow && {
          boxShadow: `0 0 20px ${theme.colors.primaryDim}, 0 0 40px ${theme.colors.primaryDim}33`,
        }),
        ...style,
      }}
    >
      {children}
      <style>{`
        .pixel-card:hover {
          background-color: ${hover ? theme.colors.cardHover : 'transparent'} !important;
          border-color: ${hover ? theme.colors.borderLight : theme.colors.border} !important;
          transform: ${hover ? 'translate(-2px, -2px)' : 'none'};
          box-shadow: ${hover ? '4px 4px 0 rgba(0,0,0,0.3)' : 'none'};
        }
      `}</style>
    </div>
  );
}

// =============================================================================
// MOCK DATA FOR DEMO (replace with actual fetch)
// =============================================================================

const mockGitHubData = {
  meta: {
    exportedAt: new Date().toISOString(),
    since: '2023-01-01',
    reposCount: 24,
  },
  aggregates: {
    totalCommits: 1847,
    totalAdditions: 127450,
    totalDeletions: 48230,
    avgLinesPerCommit: 95,
    commitsPerRepo: 77,
    streaks: {
      currentStreak: 3,
      longestStreak: 31,
      totalDays: 342,
    },
    hourlyPattern: {
      labels: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
      hours: ['8a', '10a', '12p', '2p', '4p', '6p', '8p'],
      data: [
        [15, 35, 55, 70, 65, 30, 10],  // 8a
        [25, 55, 70, 85, 75, 40, 15],  // 10a
        [35, 65, 75, 80, 70, 45, 20],  // 12p
        [45, 75, 80, 90, 75, 50, 25],  // 2p
        [40, 70, 65, 75, 60, 42, 22],  // 4p
        [30, 55, 50, 55, 45, 32, 18],  // 6p
        [15, 30, 25, 30, 25, 18, 12],  // 8p
      ],
    },
    weeklyTrend: [
      { week: '2024-08-19', commits: 42, additions: 3400 },
      { week: '2024-08-26', commits: 38, additions: 2900 },
      { week: '2024-09-02', commits: 45, additions: 3800 },
      { week: '2024-09-09', commits: 51, additions: 4200 },
      { week: '2024-09-16', commits: 47, additions: 3950 },
      { week: '2024-09-23', commits: 53, additions: 4400 },
      { week: '2024-09-30', commits: 48, additions: 4100 },
      { week: '2024-10-07', commits: 55, additions: 4600 },
      { week: '2024-10-14', commits: 52, additions: 4350 },
      { week: '2024-10-21', commits: 58, additions: 4800 },
      { week: '2024-10-28', commits: 61, additions: 5100 },
      { week: '2024-11-04', commits: 57, additions: 4750 },
    ],
    prs: {
      total: 127,
      merged: 112,
      open: 8,
      closed: 7,
      mergeRate: 88,
      avgTimeToMerge: 6.8,
      avgAdditionsPerPR: 420,
    },
    issues: {
      total: 89,
      open: 12,
      closed: 77,
      created: 89,
      avgTimeToClose: 3.2,
    },
    topChurnFiles: [
      { file: 'AuthService.tsx', commits: 45, totalChanges: 8900, churnScore: 5340 },
      { file: 'PaymentFlow.tsx', commits: 38, totalChanges: 7200, churnScore: 4520 },
      { file: 'Dashboard.jsx', commits: 32, totalChanges: 5400, churnScore: 3680 },
      { file: 'APIRoutes.js', commits: 28, totalChanges: 4100, churnScore: 3210 },
      { file: 'UserSettings.tsx', commits: 25, totalChanges: 3800, churnScore: 2880 },
    ],
    languages: {
      TypeScript: 12,
      JavaScript: 6,
      Python: 3,
      Go: 2,
      Rust: 1,
    },
  },
};

// Current OTEL data (for comparison)
const mockOtelData = {
  avgLinesPerSession: 95,
  avgLinesPerToken: 0.0045,
  tokenEfficiency: { ratio: 222 },
  prEfficiency: { mergeRate: 91, avgMergeTime: 4.2, created: 23 },
  reversionRate: { overall: 8.4 },
  sessions: { total: 127, avgDuration: 42, streak: 5 },
};

// =============================================================================
// COMPARISON METRIC CARD
// =============================================================================

function ComparisonMetric({ label, githubValue, otelValue, unit, format, inverse }) {
  const formatValue = (val) => format ? format(val) : val;
  const rawG = typeof githubValue === 'number' ? githubValue : parseFloat(githubValue);
  const rawO = typeof otelValue === 'number' ? otelValue : parseFloat(otelValue);

  // Calculate which is better
  let better;
  if (inverse) {
    better = rawG < rawO ? 'github' : rawO < rawG ? 'otel' : 'tie';
  } else {
    better = rawG > rawO ? 'github' : rawO > rawG ? 'otel' : 'tie';
  }

  const pctChange = rawO !== 0 ? ((rawO - rawG) / rawG * 100).toFixed(1) : 0;
  const isPositive = better === 'otel';

  return (
    <div style={{
      padding: '12px',
      backgroundColor: theme.colors.bg,
      border: `1px solid ${theme.colors.border}`,
      fontFamily: theme.fonts.body,
    }}>
      <div style={{ fontSize: '12px', color: theme.colors.textMuted, marginBottom: '4px' }}>
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px' }}>
        <span style={{
          fontSize: '20px',
          color: theme.colors.secondary,
        }}>
          {formatValue(githubValue)}
        </span>
        <span style={{ fontSize: '14px', color: theme.colors.textDim }}>
          {unit}
        </span>
        <span style={{ fontSize: '14px', color: theme.colors.textDim }}>→</span>
        <span style={{
          fontSize: '20px',
          color: better === 'otel' ? theme.colors.primary :
                 better === 'github' ? theme.colors.secondary :
                 theme.colors.textMuted,
        }}>
          {formatValue(otelValue)}
        </span>
        <span style={{ fontSize: '14px', color: theme.colors.textDim }}>
          {unit}
        </span>
      </div>
      <div style={{
        fontSize: '12px',
        color: isPositive ? theme.colors.success : theme.colors.warning,
        marginTop: '2px',
      }}>
        {pctChange > 0 ? '↑' : pctChange < 0 ? '↓' : '='} {Math.abs(pctChange)}% with AI
        {better === 'otel' && ' 🎯'}
      </div>
    </div>
  );
}

// =============================================================================
// SUB-COMPONENTS
// =============================================================================

// Header
function Header() {
  return (
    <header style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '20px 24px',
      borderBottom: `2px solid ${theme.colors.border}`,
      background: `linear-gradient(180deg, ${theme.colors.card} 0%, ${theme.colors.bg} 100%)`,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
        <Link to="/otel" style={{
          color: theme.colors.textMuted,
          textDecoration: 'none',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontFamily: theme.fonts.body,
          fontSize: '18px',
        }}>
          ← BACK TO METRICS
        </Link>
        <div style={{
          width: '2px',
          height: '24px',
          backgroundColor: theme.colors.border
        }} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: '28px' }}>⚡</span>
          <h1 style={{
            fontFamily: theme.fonts.header,
            fontSize: '18px',
            color: theme.colors.text,
            margin: 0,
          }}>
            BENCHMARK
          </h1>
        </div>
      </div>

      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        fontFamily: theme.fonts.body,
        fontSize: '14px',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{
            width: '12px',
            height: '12px',
            backgroundColor: theme.colors.secondary,
          }}></span>
          <span style={{ color: theme.colors.textMuted }}>GitHub History</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <span style={{
            width: '12px',
            height: '12px',
            backgroundColor: theme.colors.primary,
          }}></span>
          <span style={{ color: theme.colors.textMuted }}>OTEL Live</span>
        </div>
      </div>
    </header>
  );
}

// Overview card - high level comparison
function OverviewCard({ github, otel }) {
  const gh = github.aggregates;
  const meta = github.meta;
  return (
    <PixelCard className="p-6">
      <h2 style={{
        fontFamily: theme.fonts.header,
        fontSize: '12px',
        color: theme.colors.textMuted,
        marginBottom: '16px',
      }}>
        HISTORICAL VS CURRENT
      </h2>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '12px',
      }}>
        <ComparisonMetric
          label="Output per unit"
          githubValue={gh.avgLinesPerCommit}
          otelValue={otel.avgLinesPerSession}
          unit="lines"
        />
        <ComparisonMetric
          label="PR Success Rate"
          githubValue={gh.prs.mergeRate}
          otelValue={otel.prEfficiency.mergeRate}
          unit="%"
        />
        <ComparisonMetric
          label="PR Merge Speed"
          githubValue={gh.prs.avgTimeToMerge}
          otelValue={otel.prEfficiency.avgMergeTime}
          unit="hrs"
          inverse={true}
        />
        <ComparisonMetric
          label="Active Streak"
          githubValue={gh.streaks.currentStreak}
          otelValue={otel.sessions.streak}
          unit="days"
        />
        <ComparisonMetric
          label="Total PRs Created"
          githubValue={gh.prs.total}
          otelValue={otel.prEfficiency.created}
          unit=""
        />
        <ComparisonMetric
          label="Code Stability"
          githubValue="N/A"
          otelValue={otel.reversionRate.overall}
          unit="% reversion"
        />
      </div>

      <div style={{
        marginTop: '12px',
        padding: '10px',
        backgroundColor: theme.colors.secondaryDim,
        border: `1px solid ${theme.colors.secondary}`,
        fontFamily: theme.fonts.body,
        fontSize: '14px',
        textAlign: 'center',
        color: theme.colors.text,
      }}>
        📊 Based on <strong>{meta.reposCount}</strong> repos since{' '}
        <strong>{meta.since.split('-')[0]}</strong>
      </div>
    </PixelCard>
  );
}

// Activity pattern comparison
function ActivityComparisonCard({ github, otel }) {
  // Merge the two patterns for comparison
  const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const githubPattern = github.hourlyPattern.data.map(row =>
    row.reduce((sum, val) => sum + val, 0) / row.length
  );

  // OTEL pattern (from sessions data)
  const otelPattern = otel.sessions.weeklyData?.map(d =>
    (d.tokens || d.minutes) / 100 || 0
  ) || [65, 88, 82, 95, 85, 42, 25];

  const chartData = days.map((day, i) => ({
    day,
    github: Math.round(githubPattern[i] || 0),
    otel: Math.round(otelPattern[i] || 0),
  }));

  return (
    <PixelCard className="p-6">
      <h2 style={{
        fontFamily: theme.fonts.header,
        fontSize: '12px',
        color: theme.colors.textMuted,
        marginBottom: '16px',
      }}>
        ACTIVITY PATTERN COMPARISON
      </h2>

      <div style={{ height: '140px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartData} barSize={30}>
            <XAxis
              dataKey="day"
              style={{ fontFamily: theme.fonts.body, fontSize: '12px' }}
              stroke={theme.colors.textDim}
            />
            <YAxis
              style={{ fontFamily: theme.fonts.body, fontSize: '10px' }}
              stroke={theme.colors.textDim}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: theme.colors.card,
                border: `2px solid ${theme.colors.border}`,
                fontFamily: theme.fonts.body,
              }}
            />
            <Bar dataKey="github" fill={theme.colors.secondary} name="GitHub" />
            <Bar dataKey="otel" fill={theme.colors.primary} name="OTEL" />
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div style={{
        marginTop: '12px',
        padding: '10px',
        backgroundColor: theme.colors.bg,
        border: `1px solid ${theme.colors.border}`,
        fontFamily: theme.fonts.body,
        fontSize: '12px',
        color: theme.colors.textMuted,
      }}>
        💡 Your activity patterns have{' '}
        <span style={{ color: theme.colors.success }}>
          {Math.abs(chartData[1].otel - chartData[1].github) < 10 ? 'remained consistent' : 'shifted'}
        </span>
        {' '}between historical and current periods.
      </div>
    </PixelCard>
  );
}

// Weekly trend comparison
function TrendComparisonCard({ github }) {
  const chartData = github.weeklyTrend.map(w => ({
    week: new Date(w.week).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    commits: w.commits,
    additions: Math.round(w.additions / 100),
  }));

  return (
    <PixelCard className="p-6">
      <h2 style={{
        fontFamily: theme.fonts.header,
        fontSize: '12px',
        color: theme.colors.textMuted,
        marginBottom: '16px',
      }}>
        HISTORICAL TREND
      </h2>

      <div style={{ height: '120px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData}>
            <XAxis
              dataKey="week"
              style={{ fontFamily: theme.fonts.body, fontSize: '10px' }}
              stroke={theme.colors.textDim}
            />
            <YAxis
              yAxisId="commits"
              orientation="left"
              style={{ fontFamily: theme.fonts.body, fontSize: '10px' }}
              stroke={theme.colors.textDim}
            />
            <YAxis
              yAxisId="additions"
              orientation="right"
              style={{ fontFamily: theme.fonts.body, fontSize: '10px' }}
              stroke={theme.colors.textDim}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: theme.colors.card,
                border: `2px solid ${theme.colors.border}`,
                fontFamily: theme.fonts.body,
              }}
            />
            <Area
              yAxisId="commits"
              type="monotone"
              dataKey="commits"
              stroke={theme.colors.secondary}
              fill={theme.colors.secondary}
              fillOpacity={0.3}
            />
            <Area
              yAxisId="additions"
              type="monotone"
              dataKey="additions"
              stroke={theme.colors.primary}
              fill={theme.colors.primary}
              fillOpacity={0.3}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '16px',
        marginTop: '8px',
        fontFamily: theme.fonts.body,
        fontSize: '12px',
      }}>
        <span style={{ color: theme.colors.textMuted }}>
          <span style={{ color: theme.colors.secondary }}>■</span> Commits
        </span>
        <span style={{ color: theme.colors.textMuted }}>
          <span style={{ color: theme.colors.primary }}>■</span> Additions (×100)
        </span>
      </div>
    </PixelCard>
  );
}

// File churn comparison
function ChurnCard({ github, otel }) {
  // GitHub top churn files
  const githubChurn = github.topChurnFiles.slice(0, 5);

  return (
    <PixelCard className="p-6">
      <h2 style={{
        fontFamily: theme.fonts.header,
        fontSize: '12px',
        color: theme.colors.textMuted,
        marginBottom: '16px',
      }}>
        FILE CHURN (GITHUB HISTORY)
      </h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {githubChurn.map((f, i) => (
          <div key={i} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px',
            backgroundColor: theme.colors.bg,
            border: `1px solid ${theme.colors.border}`,
            fontFamily: theme.fonts.body,
            fontSize: '14px',
          }}>
            <span style={{
              minWidth: '20px',
              color: i < 3 ? theme.colors.secondary : theme.colors.textDim,
            }}>
              {i + 1}
            </span>
            <span style={{
              flex: 1,
              color: theme.colors.text,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {f.file}
            </span>
            <span style={{
              color: f.commits > 30 ? theme.colors.warning : theme.colors.textMuted,
              fontSize: '12px',
            }}>
              {f.commits} edits
            </span>
          </div>
        ))}
      </div>

      <div style={{
        marginTop: '12px',
        padding: '10px',
        backgroundColor: theme.colors.bg,
        border: `1px solid ${theme.colors.border}`,
        fontFamily: theme.fonts.body,
        fontSize: '12px',
        color: theme.colors.textMuted,
      }}>
        💡 Consider comparing this with OTEL reversion data to identify problematic files.
      </div>
    </PixelCard>
  );
}

// Streak comparison
function StreakCard({ github, otel }) {
  const gh = github.aggregates;
  const meta = github.meta;
  const githubStreak = gh.streaks;
  const otelStreak = otel.sessions;

  return (
    <PixelCard className="p-6">
      <h2 style={{
        fontFamily: theme.fonts.header,
        fontSize: '12px',
        color: theme.colors.textMuted,
        marginBottom: '16px',
      }}>
        STREAK ANALYSIS
      </h2>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '12px',
        marginBottom: '16px',
      }}>
        {/* Current Streak */}
        <div style={{
          padding: '16px',
          backgroundColor: theme.colors.bg,
          border: `2px solid ${theme.colors.border}`,
          textAlign: 'center',
        }}>
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '12px',
            color: theme.colors.textMuted,
            marginBottom: '8px',
          }}>
            CURRENT
          </div>
          <div style={{
            fontFamily: theme.fonts.header,
            fontSize: '32px',
            color: githubStreak.currentStreak > otelStreak.streak ? theme.colors.secondary : theme.colors.primary,
          }}>
            {otelStreak.streak}
          </div>
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '12px',
            color: theme.colors.textMuted,
          }}>
            days
          </div>
        </div>

        {/* Longest Streak */}
        <div style={{
          padding: '16px',
          backgroundColor: theme.colors.bg,
          border: `2px solid ${theme.colors.border}`,
          textAlign: 'center',
        }}>
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '12px',
            color: theme.colors.textMuted,
            marginBottom: '8px',
          }}>
            LONGEST (GH)
          </div>
          <div style={{
            fontFamily: theme.fonts.header,
            fontSize: '32px',
            color: theme.colors.secondary,
          }}>
            {githubStreak.longestStreak}
          </div>
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '12px',
            color: theme.colors.textMuted,
          }}>
            days
          </div>
        </div>

        {/* Active Days */}
        <div style={{
          padding: '16px',
          backgroundColor: theme.colors.bg,
          border: `2px solid ${theme.colors.border}`,
          textAlign: 'center',
        }}>
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '12px',
            color: theme.colors.textMuted,
            marginBottom: '8px',
          }}>
            ACTIVE DAYS
          </div>
          <div style={{
            fontFamily: theme.fonts.header,
            fontSize: '32px',
            color: theme.colors.accent,
          }}>
            {githubStreak.totalDays}
          </div>
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '12px',
            color: theme.colors.textMuted,
          }}>
            since {meta.since.split('-')[0]}
          </div>
        </div>
      </div>

      {/* Insight */}
      <div style={{
        padding: '12px',
        backgroundColor: theme.colors.secondaryDim,
        border: `1px solid ${theme.colors.secondary}`,
        fontFamily: theme.fonts.body,
        fontSize: '14px',
        textAlign: 'center',
      }}>
        🔥 Your longest streak was{' '}
        <span style={{ color: theme.colors.secondary }}>
          {githubStreak.longestStreak} days
        </span>
        ! Can you beat it with AI assistance?
      </div>
    </PixelCard>
  );
}

// Language breakdown
function LanguageCard({ github }) {
  const languages = github.languages;
  const total = Object.values(languages).reduce((sum, v) => sum + v, 0);

  const chartData = Object.entries(languages)
    .map(([lang, count]) => ({
      lang,
      count,
      pct: Math.round((count / total) * 100),
    }))
    .sort((a, b) => b.count - a.count);

  const COLORS = [
    theme.colors.secondary, theme.colors.primary, theme.colors.accent,
    theme.colors.success, theme.colors.warning, theme.colors.danger,
  ];

  return (
    <PixelCard className="p-6">
      <h2 style={{
        fontFamily: theme.fonts.header,
        fontSize: '12px',
        color: theme.colors.textMuted,
        marginBottom: '16px',
      }}>
        LANGUAGE BREAKDOWN
      </h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {chartData.map((item, i) => (
          <div key={item.lang}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              fontFamily: theme.fonts.body,
              fontSize: '14px',
              color: theme.colors.textMuted,
              marginBottom: '4px',
            }}>
              <span>{item.lang}</span>
              <span>{item.pct}%</span>
            </div>
            <div style={{
              height: '8px',
              backgroundColor: theme.colors.bg,
              border: `1px solid ${theme.colors.border}`,
            }}>
              <div style={{
                width: `${item.pct}%`,
                height: '100%',
                backgroundColor: COLORS[i % COLORS.length],
              }} />
            </div>
          </div>
        ))}
      </div>
    </PixelCard>
  );
}

// Import status card
function ImportCard({ github }) {
  const gh = github.aggregates;
  const meta = github.meta;
  return (
    <PixelCard className="p-6">
      <h2 style={{
        fontFamily: theme.fonts.header,
        fontSize: '12px',
        color: theme.colors.textMuted,
        marginBottom: '16px',
      }}>
        DATA SOURCE
      </h2>

      <div style={{
        fontFamily: theme.fonts.body,
        fontSize: '14px',
        color: theme.colors.text,
      }}>
        <div style={{ marginBottom: '12px' }}>
          <span style={{ color: theme.colors.textMuted }}>Repositories:</span>{' '}
          <strong>{meta.reposCount}</strong>
        </div>
        <div style={{ marginBottom: '12px' }}>
          <span style={{ color: theme.colors.textMuted }}>Commits analyzed:</span>{' '}
          <strong>{gh.totalCommits.toLocaleString()}</strong>
        </div>
        <div style={{ marginBottom: '12px' }}>
          <span style={{ color: theme.colors.textMuted }}>Date range:</span>{' '}
          <strong>{meta.since}</strong> to now
        </div>
        <div style={{ marginBottom: '16px' }}>
          <span style={{ color: theme.colors.textMuted }}>Last updated:</span>{' '}
          <strong>{new Date(meta.exportedAt).toLocaleDateString()}</strong>
        </div>

        <div style={{
          padding: '10px',
          backgroundColor: theme.colors.primaryDim,
          border: `1px solid ${theme.colors.primary}`,
          fontFamily: theme.fonts.body,
          fontSize: '12px',
          color: theme.colors.text,
        }}>
          💡 To update this data, run:{' '}
          <code style={{
            backgroundColor: theme.colors.bg,
            padding: '2px 6px',
            borderRadius: '2px',
          }}>node scripts/import-github-data.js</code>
        </div>
      </div>
    </PixelCard>
  );
}

// =============================================================================
// MAIN PAGE
// =============================================================================

export default function BenchmarkPage() {
  const [githubData, setGithubData] = useState(null);
  const [otelData, setOtelData] = useState(mockOtelData);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Try to fetch GitHub data, fall back to mock
    fetch('/data/github-history.json')
      .then(res => {
        if (res.ok) return res.json();
        throw new Error('Using mock data');
      })
      .then(data => setGithubData(data))
      .catch(() => setGithubData(mockGitHubData))
      .finally(() => setLoading(false));
  }, []);

  if (loading || !githubData) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: theme.colors.bg,
        fontFamily: theme.fonts.header,
        fontSize: '14px',
        color: theme.colors.primary,
      }}>
        {loading ? 'LOADING BENCHMARK DATA...' : 'NO GITHUB DATA FOUND - RUN THE IMPORTER'}
      </div>
    );
  }

  return (
    <div style={{
      backgroundColor: theme.colors.bg,
      minHeight: '100vh',
      fontFamily: theme.fonts.body,
      color: theme.colors.text,
    }}>
      <Header />

      <main style={{
        padding: '24px',
        maxWidth: '1400px',
        margin: '0 auto',
      }}>
        {/* Controls bar */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '24px',
        }}>
          <div style={{
            fontFamily: theme.fonts.body,
            fontSize: '14px',
            color: theme.colors.textMuted,
          }}>
            Comparing your GitHub history with current OTEL metrics
          </div>
        </div>

        {/* Dashboard grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(12, 1fr)',
          gap: '16px',
        }}>

          {/* Top row - Overview */}
          <div style={{ gridColumn: 'span 12' }}>
            <OverviewCard github={githubData} otel={otelData} />
          </div>

          {/* Second row */}
          <div style={{ gridColumn: 'span 6' }}>
            <ActivityComparisonCard github={githubData.aggregates} otel={otelData} />
          </div>

          <div style={{ gridColumn: 'span 6' }}>
            <TrendComparisonCard github={githubData.aggregates} />
          </div>

          {/* Third row */}
          <div style={{ gridColumn: 'span 4' }}>
            <StreakCard github={githubData} otel={otelData} />
          </div>

          <div style={{ gridColumn: 'span 4' }}>
            <ChurnCard github={githubData.aggregates} otel={otelData} />
          </div>

          <div style={{ gridColumn: 'span 4' }}>
            <LanguageCard github={githubData.aggregates} />
          </div>

          {/* Fourth row - Import info */}
          <div style={{ gridColumn: 'span 12' }}>
            <ImportCard github={githubData} />
          </div>

        </div>

        {/* Footer */}
        <footer style={{
          marginTop: '32px',
          padding: '16px',
          borderTop: `2px solid ${theme.colors.border}`,
          textAlign: 'center',
          fontFamily: theme.fonts.body,
          fontSize: '14px',
          color: theme.colors.textDim,
        }}>
          <div>
            ⚡ GitHub Benchmark • Compares historical data with OTEL live metrics
          </div>
        </footer>
      </main>
    </div>
  );
}
